import { dedupeTabsById } from '../utils/layoutTabUtils';
import { mainContentTabSupportsTarget } from '../utils/target-scope.utils';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type {
  MainContentTab,
  SidebarTab,
  Tab,
  TabGroup,
  TabNavigationState,
  LayoutState,
} from './layoutStoreTypes';

export function filterTabsForTargetType({
  tabs,
  targetType,
}: {
  tabs: Tab[];
  targetType: TargetTypeType | undefined;
}): Tab[] {
  if (!targetType) {
    return tabs;
  }

  return tabs.filter((tab) =>
    mainContentTabSupportsTarget({ tabType: tab.type, targetType }),
  );
}

export function pickNavigationForTabIds({
  tabNavigationByTabId,
  tabIds,
}: {
  tabNavigationByTabId: Record<string, TabNavigationState>;
  tabIds: ReadonlySet<string>;
}): Record<string, TabNavigationState> {
  return Object.fromEntries(
    Object.entries(tabNavigationByTabId).filter(([tabId]) => tabIds.has(tabId)),
  );
}

export function omitNavigationForTabIds({
  tabNavigationByTabId,
  tabIds,
}: {
  tabNavigationByTabId: Record<string, TabNavigationState>;
  tabIds: ReadonlySet<string>;
}): Record<string, TabNavigationState> {
  return Object.fromEntries(
    Object.entries(tabNavigationByTabId).filter(
      ([tabId]) => !tabIds.has(tabId),
    ),
  );
}

export function partitionTabGroupsForAppTabs({
  tabGroups,
  appTabIds,
}: {
  tabGroups: TabGroup[];
  appTabIds: ReadonlySet<string>;
}): {
  parkedGroups: TabGroup[];
  remainingGroups: TabGroup[];
} {
  const parkedGroups: TabGroup[] = [];
  const remainingGroups: TabGroup[] = [];

  for (const group of tabGroups) {
    const parkedTabIds = group.tabIds.filter((tabId) => appTabIds.has(tabId));
    const remainingTabIds = group.tabIds.filter(
      (tabId) => !appTabIds.has(tabId),
    );

    if (parkedTabIds.length > 0) {
      parkedGroups.push({
        ...group,
        tabIds: parkedTabIds,
      });
    }

    if (remainingTabIds.length > 0) {
      remainingGroups.push({
        ...group,
        tabIds: remainingTabIds,
      });
    }
  }

  return { parkedGroups, remainingGroups };
}

export function mergeTabsByOrder({
  restoredAppTabs,
  globalTabs,
  tabOrder,
}: {
  restoredAppTabs: Tab[];
  globalTabs: Tab[];
  tabOrder: string[];
}): Tab[] {
  const appById = new Map(restoredAppTabs.map((tab) => [tab.id, tab]));
  const globalById = new Map(globalTabs.map((tab) => [tab.id, tab]));
  const usedIds = new Set<string>();
  const merged: Tab[] = [];

  for (const tabId of tabOrder) {
    const appTab = appById.get(tabId);

    if (appTab) {
      merged.push(appTab);
      usedIds.add(tabId);
      continue;
    }

    const globalTab = globalById.get(tabId);

    if (globalTab) {
      merged.push(globalTab);
      usedIds.add(tabId);
    }
  }

  for (const tab of restoredAppTabs) {
    if (!usedIds.has(tab.id)) {
      merged.push(tab);
      usedIds.add(tab.id);
    }
  }

  for (const tab of globalTabs) {
    if (!usedIds.has(tab.id)) {
      merged.push(tab);
    }
  }

  return merged;
}

export function resolveActiveTabIdAfterTargetSwitch({
  finalTabIds,
  preferredActiveTabId,
  tabHistory,
  restoredAppTabs,
  finalTabs,
  wasActiveTabClosed,
}: {
  finalTabIds: ReadonlySet<string>;
  preferredActiveTabId: string | undefined;
  tabHistory: string[];
  restoredAppTabs: Tab[];
  finalTabs: Tab[];
  wasActiveTabClosed: boolean;
}): string | undefined {
  if (preferredActiveTabId && finalTabIds.has(preferredActiveTabId)) {
    return preferredActiveTabId;
  }

  if (!wasActiveTabClosed) {
    const stillActive = finalTabs.find((tab) => tab.isActive);

    if (stillActive) {
      return stillActive.id;
    }
  }

  for (let index = tabHistory.length - 1; index >= 0; index -= 1) {
    const historyTabId = tabHistory[index];

    if (finalTabIds.has(historyTabId)) {
      return historyTabId;
    }
  }

  if (restoredAppTabs.length > 0) {
    return restoredAppTabs[0].id;
  }

  if (finalTabs.length > 0) {
    return finalTabs[finalTabs.length - 1].id;
  }

  return undefined;
}

export function resolveSidebarTabForActiveMainContent({
  tabs,
  activeMainContentTabId,
  tabNavigationByTabId,
  getSidebarTabForMainContentTab,
}: {
  tabs: Tab[];
  activeMainContentTabId: string | undefined;
  tabNavigationByTabId: LayoutState['tabNavigationByTabId'];
  getSidebarTabForMainContentTab: (
    tabType: MainContentTab,
  ) => SidebarTab | null;
}): SidebarTab | undefined {
  const activeTab =
    (activeMainContentTabId
      ? tabs.find((tab) => tab.id === activeMainContentTabId)
      : undefined) ?? tabs.find((tab) => tab.isActive);

  if (!activeTab) {
    return undefined;
  }

  const tabNavigation = tabNavigationByTabId[activeTab.id];
  const pageEntries = tabNavigation?.entries ?? [];
  const startIndex =
    tabNavigation?.currentIndex ?? Math.max(pageEntries.length - 1, 0);

  for (let index = startIndex; index >= 0; index -= 1) {
    const pageType = pageEntries[index]?.type;

    if (!pageType) {
      continue;
    }

    const sidebarTab = getSidebarTabForMainContentTab(pageType);

    if (sidebarTab) {
      return sidebarTab;
    }
  }

  return getSidebarTabForMainContentTab(activeTab.type) ?? undefined;
}

export function dedupeApplicationTabs({
  tabs,
  applicationSpecificTabTypes,
}: {
  tabs: Tab[];
  applicationSpecificTabTypes: readonly MainContentTab[];
}): Tab[] {
  return dedupeTabsById({
    tabs: tabs.filter((tab) => applicationSpecificTabTypes.includes(tab.type)),
  });
}
